import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { dayLabel, localDateOf } from '@kronos/domain'
import type { Shift } from '@kronos/domain'
import { repository } from '../../data'
import { clockTime, endOnShiftDay, problemOf } from '../hours/time'

/**
 * Yesterday's clock-out, forgotten. Asked first, before anything else on
 * Today, because until it is answered that shift counts for nothing on the
 * pay report — and the person who knows when they left is the one holding
 * the phone, this morning, not the accountant in two weeks.
 *
 * One field: a time. The date is the day the shift started (`endOnShiftDay`).
 */
export function ForgottenShift({ shift, onFixed }: { shift: Shift; onFixed: () => void }): JSX.Element {
  const [time, setTime] = useState('')
  const [problem, setProblem] = useState('')
  const day = dayLabel(localDateOf(shift.startedAt, Intl.DateTimeFormat().resolvedOptions().timeZone))

  const save = useMutation({
    mutationFn: async () => {
      const endedAt = endOnShiftDay(shift.startedAt, time)
      if (!endedAt) throw new Error('Type the time you left.')
      return repository.editShift(shift.id, { startedAt: shift.startedAt, endedAt })
    },
    onSuccess: onFixed,
    onError: (e) => setProblem(problemOf(e)),
  })

  return (
    <section className="clock-card clock-card--attention" aria-label="Forgotten clock-out">
      <span className="stack" style={{ gap: 4 }}>
        <strong>You never clocked out on {day}.</strong>
        <span className="muted">
          You started at {clockTime(shift.startedAt)}. When did you leave? Until you say, that day isn’t counted.
        </span>
      </span>
      <label className="field">
        <span className="field-label">I left at</span>
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </label>
      <button className="clock-card__button primary" disabled={save.isPending || time === ''} onClick={() => save.mutate()}>
        Save
      </button>
      {problem ? (
        <p className="critical" role="alert">
          {problem}
        </p>
      ) : null}
    </section>
  )
}
